import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ApiService from "../../api/apiService";
import { convertTo12HourPlusDateFormat } from "../../util/helperFunctions";
import "./view-request-detail.css";
import ImagePreviewModal from "./reusable/image-preview-modal";

function CCARRequestDetail({
  setApproversData,
  userDetails,
}) {
  const location = useLocation();

  const [ccarData, setCCARData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returnUrl, setReturnUrl] = useState("/");
  const [isUserApprover, setUserApprover] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const handleApproveDenyRequest = async (status) => {
    try {
      setLoading(true);

      const approvalResponse =
        await ApiService.forms.approveDenyRequest(
          ccarData?.form_id,
          ccarData?.form_request_id,
          status,
        );

      console.log(
        "APPROVE/DENY RESPONSE:",
        approvalResponse,
      );

      if (!approvalResponse?.success) {
        console.error(
          "The approval request was not successful.",
        );
        return;
      }

      const searchParams = new URLSearchParams(
        location.search,
      );

      const requestType = searchParams.get("t");
      const requestLinkId = searchParams.get("r");

      const updatedResponse =
        await ApiService.forms.getFormRequestDetail(
          requestType,
          requestLinkId,
        );

      console.log(
        "UPDATED APPROVAL DATA:",
        JSON.stringify(
          updatedResponse?.request?.approval,
          null,
          2,
        ),
      );

      if (
        updatedResponse?.success &&
        updatedResponse?.request
      ) {
        const updatedRequest = {
          ...updatedResponse.request,
          prospecting_images:
            updatedResponse.request
              ?.prospecting_images ?? "",
        };

        setCCARData(updatedRequest);
        setApproversData?.(
          updatedRequest?.approval,
        );
        setUserApprover(false);
      }
    } catch (error) {
      console.error(
        "Failed to approve or deny CCAR request:",
        error,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getCCARRequest = async () => {
      try {
        setLoading(true);

        const searchParams = new URLSearchParams(
          location.search,
        );

        const requestType = searchParams.get("t");
        const requestLinkId = searchParams.get("r");
        const requestReturnUrl =
          searchParams.get("r_url");

        setReturnUrl(requestReturnUrl || "/");

        if (!requestType || !requestLinkId) {
          setCCARData(null);
          return;
        }

        const response =
          await ApiService.forms.getFormRequestDetail(
            requestType,
            requestLinkId,
          );

        console.log(
          "CCAR RESPONSE:",
          response?.request,
        );

        console.log(
          "FULL APPROVAL DATA:",
          JSON.stringify(
            response?.request?.approval,
            null,
            2,
          ),
        );

        if (!response?.success || !response?.request) {
          setCCARData(null);
          return;
        }

        const request = {
          ...response.request,
          prospecting_images:
            response.request?.prospecting_images ?? "",
        };

        setCCARData(request);

        if (setApproversData) {
          setApproversData(request?.approval);
        }
      } catch (error) {
        console.error(
          "Failed to retrieve CCAR request:",
          error,
        );

        setCCARData(null);
      } finally {
        setLoading(false);
      }
    };

    getCCARRequest();
  }, [location.search, setApproversData]);

  useEffect(() => {
    if (!userDetails || !ccarData) {
      setUserApprover(false);
      return;
    }

    const {
      approval,
      overall_approval_status,
      reject_status,
    } = ccarData;

    const {
      first_level_approval = [],
      second_level_approval = [],
    } = approval || {};

    const firstLevelApprover =
      first_level_approval.find(
        (approver) =>
          approver?.id === userDetails?.id,
      );

    const secondLevelApprover =
      second_level_approval.find(
        (approver) =>
          approver?.id === userDetails?.id,
      );

    const canVoteFirstLevel =
      firstLevelApprover &&
      !firstLevelApprover?.has_voted;

    const canVoteSecondLevel =
      secondLevelApprover &&
      !secondLevelApprover?.has_voted;

    const requestIsPending =
      !overall_approval_status &&
      !reject_status &&
      ccarData?.final_status === "Pending";

    setUserApprover(
      Boolean(
        requestIsPending &&
        (canVoteFirstLevel ||
          canVoteSecondLevel),
      ),
    );
  }, [userDetails, ccarData]);

  const getApproverName = (approver) => {
    if (approver?.display_name) {
      return approver.display_name;
    }

    const fullName = [
      approver?.first_name,
      approver?.middle_name,
      approver?.last_name,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    return fullName || "Unknown Approver";
  };

  const isTruthyValue = (value) => {
    return (
      value === true ||
      value === 1 ||
      value === "1" ||
      String(value).toLowerCase() === "true"
    );
  };

  const getApprovedApprovers = () => {
    const firstLevelApprovers =
      ccarData?.approval
        ?.first_level_approval || [];

    const secondLevelApprovers =
      ccarData?.approval
        ?.second_level_approval || [];

    const approvedApprovers = [
      ...firstLevelApprovers,
      ...secondLevelApprovers,
    ].filter((approver) => {
      return (
        isTruthyValue(approver?.approved) &&
        isTruthyValue(approver?.has_voted)
      );
    });

    return approvedApprovers.filter(
      (approver, index, approvers) =>
        approvers.findIndex(
          (item) => item?.id === approver?.id,
        ) === index,
    );
  };

  const getAttachments = () => {
    if (!ccarData?.prospecting_images) {
      return [];
    }

    try {
      let attachments = JSON.parse(
        ccarData.prospecting_images,
      );

      // Handle double-stringified JSON.
      if (typeof attachments === "string") {
        attachments = JSON.parse(attachments);
      }

      if (!Array.isArray(attachments)) {
        attachments = [attachments];
      }

      return attachments.filter(Boolean);
    } catch (error) {
      return [ccarData.prospecting_images].filter(
        Boolean,
      );
    }
  };

  const renderAttachment = (
    attachment,
    index,
  ) => {
    if (!attachment) {
      return null;
    }

    const cleanUrl = attachment
      .split("?")[0]
      .toLowerCase();

    const isImage =
      /\.(png|jpe?g|gif|webp|bmp)$/i.test(
        cleanUrl,
      );

    const isPdf = /\.pdf$/i.test(cleanUrl);

    if (isImage) {
      return (
        <div
          className="attachment-item"
          key={`${attachment}-${index}`}
        >
          <img
            src={attachment}
            alt={`Attachment ${index + 1}`}
            className="ccar-image-preview bh-clickable-image"
            onClick={() =>
              setPreviewImage(attachment)
            }
          />
        </div>
      );
    }

    if (isPdf) {
      return (
        <div
          className="attachment-item"
          key={`${attachment}-${index}`}
        >
          <div
            className="ccar-pdf-preview bh-clickable-image"
            onClick={() =>
              window.open(
                attachment,
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            <i className="fa fa-file-pdf-o fa-3x" />
            <span>PDF</span>
          </div>
        </div>
      );
    }

    return (
      <div
        className="attachment-item"
        key={`${attachment}-${index}`}
      >
        <a
          href={attachment}
          target="_blank"
          rel="noopener noreferrer"
          className="attachment-btn"
        >
          <i className="fa fa-file-o fa-3x" />
          <br />
          Open File
        </a>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loader--container">
        <div className="loader--"></div>
      </div>
    );
  }

  if (!ccarData) {
    return (
      <div className="request-detail--card">
        <h2>CCAR Request Not Found</h2>
      </div>
    );
  }

  const approvedApprovers =
    getApprovedApprovers();

  const attachments = getAttachments();

  const isRequestApproved =
    ccarData?.overall_approval_status === true ||
    ccarData?.final_status === "Approved";

  const isRequestRejected =
    ccarData?.reject_status === true ||
    ccarData?.final_status === "Rejected";

  return (
    <div className="view-request-detail-container">
      <a href={returnUrl}>
        <div className="go-back">
          <i
            className="fa fa-arrow-left fa-3x"
            aria-hidden="true"
          ></i>

          <span>Go Back</span>
        </div>
      </a>

      <div className="request-detail-row">
        <div className="request-detail--card">
          <div className="request-detail-header">
            <h2>
              Customer Corrective Action Request
            </h2>

            <span
              className={`status ${isRequestApproved
                  ? "approved"
                  : isRequestRejected
                    ? "rejected"
                    : "pending"
                }`}
            >
              {isRequestApproved
                ? "Approved"
                : isRequestRejected
                  ? "Rejected"
                  : ccarData?.final_status ||
                  "Pending"}
            </span>
          </div>

          <hr />

          <div className="request-detail-section">
            <h3>Request Information</h3>

            <div className="detail-row">
              <label>Reference Number</label>

              <span>
                {ccarData?.reference_number ||
                  "N/A"}
              </span>
            </div>

            <div className="detail-row">
              <label>Issued To</label>

              <span>
                {ccarData?.issued_to || "N/A"}
              </span>
            </div>

            <div className="detail-row">
              <label>NC Source</label>

              <span>
                {ccarData?.nc_source || "N/A"}
              </span>
            </div>

            <div className="detail-row">
              <label>Warehouse</label>

              <span>
                {ccarData?.warehouse || "N/A"}
              </span>
            </div>

            <div className="detail-row">
              <label>Date Submitted</label>

              <span>
                {ccarData?.createdAt
                  ? convertTo12HourPlusDateFormat(
                    ccarData.createdAt,
                  )
                  : "N/A"}
              </span>
            </div>
          </div>

          {isRequestApproved && (
            <>
              <hr />

              <div className="request-detail-section">
                <h3>Approved By</h3>

                {approvedApprovers.length > 0 ? (
                  <div className="ccar-approved-by-list">
                    {approvedApprovers.map(
                      (approver) => (
                        <div
                          className="ccar-approved-by-item"
                          key={approver?.id}
                        >
                          <div className="ccar-approved-by-avatar">
                            {approver?.display_name
                              ?.charAt(0)
                              ?.toUpperCase() || "A"}
                          </div>

                          <div className="ccar-approved-by-information">
                            <strong>
                              {approver?.display_name ||
                                "Unknown Approver"}
                            </strong>

                            {approver?.date && (
                              <span>
                                Approved on{" "}
                                {convertTo12HourPlusDateFormat(
                                  approver.date,
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <div className="ccar-approved-by-empty">
                    The request is approved, but the API
                    did not return the approving user's
                    record.
                  </div>
                )}
              </div>
            </>
          )}

          <hr />

          <div className="request-detail-section">
            <h3>Corrective Action Details</h3>

            <div className="description-box">
              {ccarData?.nc_description ||
                "No description provided."}
            </div>
          </div>

          {attachments.length > 0 && (
            <>
              <hr />

              <div className="request-detail-section">
                <h3>Attachments</h3>

                <div className="request-item-attachment-container">
                  {attachments.map(
                    (attachment, index) =>
                      renderAttachment(
                        attachment,
                        index,
                      ),
                  )}
                </div>
              </div>
            </>
          )}

          {isUserApprover &&
            !ccarData?.overall_approval_status &&
            !ccarData?.reject_status &&
            ccarData?.final_status ===
            "Pending" && (
              <>
                <hr />

                <div className="approver--actions">
                  <button
                    type="button"
                    className="action-btn approve-btn"
                    onClick={() =>
                      handleApproveDenyRequest(true)
                    }
                  >
                    Approve Request
                  </button>

                  <button
                    type="button"
                    className="action-btn deny-btn"
                    onClick={() =>
                      handleApproveDenyRequest(false)
                    }
                  >
                    Deny Request
                  </button>
                </div>
              </>
            )}

          <ImagePreviewModal
            image={previewImage}
            onClose={() =>
              setPreviewImage(null)
            }
          />
        </div>
      </div>
    </div>
  );
}

export default CCARRequestDetail;